/**
 * Production Security & Sanitization Utilities
 * Prevents XSS, Script Injection, URL protocol attacks, and Domain Ambiguities.
 */

// Allowed URL schemes
const ALLOWED_SCHEMES = ['https:', 'http:', 'mailto:', 'tel:'];

/**
 * Validates and sanitizes link targets to prevent `javascript:` and `data:` URI attacks.
 */
export function sanitizeUrl(rawUrl?: string): string {
  if (!rawUrl) return '#';
  const trimmed = rawUrl.trim();

  // Allow relative anchors and paths
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed, 'https://example.com');
    if (ALLOWED_SCHEMES.includes(parsed.protocol)) {
      return trimmed;
    }
    // Blocked protocol
    console.warn(`[Security] Blocked unsafe protocol in URL: ${trimmed}`);
    return '#';
  } catch {
    // If not a valid URL or path, fallback safely
    return '#';
  }
}

/**
 * Escapes unsafe HTML characters to prevent Stored & Reflected XSS.
 */
export function escapeHtml(text?: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Normalizes domains by stripping protocols, 'www.', trailing slashes, paths, and converting to lowercase.
 * Ensures consistent domain identity: "https://WWW.Example.COM/test" -> "example.com"
 */
export function validateDomainInput(rawDomain: string): { isValid: boolean; normalized: string; error?: string } {
  if (!rawDomain || typeof rawDomain !== 'string') {
    return { isValid: false, normalized: '', error: 'Lütfen bir alan adı giriniz.' };
  }

  const trimmed = rawDomain.trim();

  // Strictly reject URLs with paths or query parameters
  // e.g. https://example.com/test or example.com/test or example.com?q=1
  if (trimmed.includes('?') || trimmed.includes('#')) {
    return { isValid: false, normalized: '', error: 'Alan adı parametre veya sorgu içeremez.' };
  }

  // Check if protocol is present
  let hostPart = trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.pathname && url.pathname !== '/' && url.pathname !== '') {
        return { isValid: false, normalized: '', error: 'URL yolu (path) girilemez. Lütfen yalnızca alan adını giriniz (örn: sirketiniz.com).' };
      }
      hostPart = url.hostname;
    } catch {
      return { isValid: false, normalized: '', error: 'Geçersiz URL formatı.' };
    }
  } else if (trimmed.includes('/')) {
    const segments = trimmed.split('/');
    if (segments.length > 1 && segments[1].trim().length > 0) {
      return { isValid: false, normalized: '', error: 'Alan adı yolu (path) içeremez. Lütfen yalnızca alan adını giriniz (örn: sirketiniz.com).' };
    }
    hostPart = segments[0];
  }

  // Remove trailing dot if present
  let normalized = hostPart.trim().toLowerCase();
  while (normalized.endsWith('.')) {
    normalized = normalized.slice(0, -1);
  }

  // Remove port if present
  normalized = normalized.replace(/:\d+$/, '');

  // Validate domain format (labels separated by dots, each label 1-63 chars, TLD at least 2 chars)
  const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
  if (!domainRegex.test(normalized) || normalized.length < 4) {
    return { isValid: false, normalized, error: 'Lütfen geçerli bir alan adı giriniz (örn: sirketiniz.com veya www.sirketiniz.com).' };
  }

  return { isValid: true, normalized };
}

/**
 * Normalizes domains by stripping protocols, ports, trailing dots, slashes and converting to lowercase.
 * Retains 'www.' if present as distinct hostname per architecture.
 */
export function normalizeDomain(rawDomain: string): string {
  if (!rawDomain) return '';
  const validation = validateDomainInput(rawDomain);
  if (validation.isValid) {
    return validation.normalized;
  }
  // Fallback cleaner for non-strict lookups
  let cleaned = rawDomain.trim().toLowerCase().replace(/^https?:\/\//i, '');
  cleaned = cleaned.split('/')[0].split('?')[0].split('#')[0].replace(/:\d+$/, '');
  while (cleaned.endsWith('.')) {
    cleaned = cleaned.slice(0, -1);
  }
  return cleaned.trim();
}

/**
 * Reserved routes that cannot be used as customer website public slugs
 */
export const RESERVED_ROUTES = new Set([
  'admin',
  'dashboard',
  'login',
  'giris',
  'kayit',
  'register',
  'signup',
  'api',
  'assets',
  'editor',
  'preview',
  'subscriptions',
  'settings',
  'domain',
  'domains',
  'ai',
  'temalar',
  'sektorler',
  'fiyatlandirma',
  'ozellikler',
  'nasil-calisir',
  'sss',
  'iletisim',
  'workspace',
  'pay',
  'sitemap',
  'robots',
  'favicon',
]);

/**
 * Normalizes a slug to lowercase, URL-safe, with Turkish character transliteration
 * (ç->c, ğ->g, ı->i, ö->o, ş->s, ü->u), whitespace replaced with hyphens,
 * and strips special characters. Protects against reserved system routes.
 */
export function normalizeSlug(rawSlug: string): string {
  if (!rawSlug) return '';
  let str = String(rawSlug).toLowerCase().trim();

  // Turkish character mapping
  const trMap: Record<string, string> = {
    'ç': 'c',
    'ğ': 'g',
    'ı': 'i',
    'i': 'i',
    'ö': 'o',
    'ş': 's',
    'ü': 'u',
    'Ç': 'c',
    'Ğ': 'g',
    'İ': 'i',
    'I': 'i',
    'Ö': 'o',
    'Ş': 's',
    'Ü': 'u',
  };
  str = str.replace(/[çğışöüÇĞİŞÖÜ]/g, (m) => trMap[m] || m);

  // Normalize diacritics
  try {
    str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    // fallback if normalize is unsupported
  }

  // Replace spaces and special characters with hyphens
  str = str.replace(/[^a-z0-9-]/g, '-');
  str = str.replace(/-+/g, '-');
  str = str.replace(/^-|-$/g, '');

  if (!str) return 'site';

  // Ensure not reserved
  if (RESERVED_ROUTES.has(str)) {
    str = `${str}-site`;
  }

  return str;
}

/**
 * Validates whether a slug is lowercase, URL-safe and alphanumeric.
 */
export function sanitizeSlug(rawSlug: string): string {
  return normalizeSlug(rawSlug);
}

/**
 * In-memory client/edge rate limiting tracker for public form submissions (e.g. reservation requests)
 */
const submissionTracker = new Map<string, number[]>();

export function checkSubmissionRateLimit(identifier: string, maxAttempts = 3, windowMs = 60000): boolean {
  const now = Date.now();
  const history = submissionTracker.get(identifier) || [];
  
  // Clean timestamps outside current window
  const validTimestamps = history.filter((t) => now - t < windowMs);
  
  if (validTimestamps.length >= maxAttempts) {
    return false; // Rate limit exceeded
  }
  
  validTimestamps.push(now);
  submissionTracker.set(identifier, validTimestamps);
  return true; // Allowed
}

/**
 * Rule 10: Checks whether the custom domain attached to the website is fully active/connected with SSL.
 * Returns true ONLY if both DNS/domain and SSL certificates are verified and active.
 */
export function isSiteDomainConnected(site?: {
  customDomain?: string;
  domainStatus?: string;
  sslStatus?: string;
  domains?: Array<{ domain?: string; status?: string; sslStatus?: string; isPrimary?: boolean; hostState?: string }>;
} | null): boolean {
  if (!site || !site.customDomain) return false;

  // Check top-level site domainStatus and sslStatus
  const isSiteDomainActive =
    site.domainStatus === 'ACTIVE' ||
    (site.domainStatus as any) === 'CONNECTED' ||
    (site.domainStatus as any) === 'DOMAIN_ACTIVE';
  const isSiteSslActive =
    site.sslStatus === 'ACTIVE' ||
    (site.sslStatus as any) === 'SSL_ACTIVE' ||
    (site.sslStatus as any) === 'CERT_ACTIVE';

  if (isSiteDomainActive && isSiteSslActive) {
    return true;
  }

  // Check domains array for primary or matching custom domain
  if (Array.isArray(site.domains)) {
    const cleanTarget = site.customDomain.trim().toLowerCase();
    const match = site.domains.find(
      (d) => (d.domain && d.domain.trim().toLowerCase() === cleanTarget) || d.isPrimary
    );
    if (match) {
      const matchDomainActive =
        match.status === 'ACTIVE' ||
        (match.status as any) === 'CONNECTED' ||
        (match.status as any) === 'DOMAIN_ACTIVE' ||
        match.hostState === 'HOST_ACTIVE';
      const matchSslActive =
        match.sslStatus === 'ACTIVE' ||
        (match.sslStatus as any) === 'SSL_ACTIVE' ||
        (match.sslStatus as any) === 'CERT_ACTIVE';
      if (matchDomainActive && matchSslActive) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns the canonical public URL for an Adiosoft customer site.
 * Follows the Single Hosting Architecture & Rule 10 (Smart Routing):
 * - Custom domain ONLY if fully connected & verified (e.g., https://example.com)
 * - Otherwise: https://www.adiosoft.com/slug (or current origin in development/preview)
 * Never creates subdomain-based URLs (no slug.adiosoft.com).
 */
export function getSitePublicUrl(
  site?: {
    customDomain?: string;
    slug?: string;
    subdomain?: string;
    domainStatus?: string;
    sslStatus?: string;
    domains?: any[];
  } | null,
  options?: { allowPendingDomain?: boolean }
): string {
  if (!site) return 'https://www.adiosoft.com/';
  const slug = site.slug || site.subdomain || 'site';

  // Rule 10: If domain is configured and verified (or allowPendingDomain override is true), route to custom domain
  if (site.customDomain && (options?.allowPendingDomain || isSiteDomainConnected(site))) {
    const clean = site.customDomain.trim().toLowerCase();
    return clean.startsWith('http') ? clean : `https://${clean}`;
  }

  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.includes('run.app'))) {
    return `${window.location.origin}/${slug}`;
  }
  return `https://www.adiosoft.com/${slug}`;
}

/**
 * Returns a human-friendly display address for a site (e.g. adiosoft.com/slug or customdomain.com)
 * Respects Rule 10: Shows customDomain only if verified or requested.
 */
export function getSiteDisplayAddress(
  site?: {
    customDomain?: string;
    slug?: string;
    subdomain?: string;
    domainStatus?: string;
    sslStatus?: string;
    domains?: any[];
  } | null,
  options?: { allowPendingDomain?: boolean }
): string {
  if (!site) return 'adiosoft.com';
  const slug = site.slug || site.subdomain || 'site';

  if (site.customDomain && (options?.allowPendingDomain || isSiteDomainConnected(site))) {
    return site.customDomain.trim().toLowerCase();
  }
  return `adiosoft.com/${slug}`;
}

